import React, { useState } from "react";
import axios from "axios";
import { Container, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import OtpInput from "react-otp-input";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { AiOutlineEdit } from "react-icons/ai";
import { LazyLoadImage } from "react-lazy-load-image-component";
import IconHeader from "../../images/logo-lg.png";
import QuoteSlider from "./QuoteSlider";
import "./style.scss";
import apiConfig from "./apiConfig";


const quotes = [
  "Easily discover and quickly benefit from the most advanced technologies.",
  "Expert-level insights at your fingertips.",
  "Expedite planning, fieldwork and completion procedures.",
  "Elevate your expertise and confidence.",
  "AI-powered audits: Save time, increase profitability, and improve quality.",
];

function OTPScreen() {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // New state for cooldown timers
  const [submitCooldown, setSubmitCooldown] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);

  const email = location.state?.email;
  const userId = location.state?.userId;
  const signUp = location.state?.signUp || false;

  const handleOtpChange = (otpValue) => {
    if (/^\d{0,5}$/.test(otpValue)) {
      setOtp(otpValue);
    }
  };

  // Helper function to start a cooldown timer
  const startCooldown = (setCooldown) => {
    setCooldown(15);
    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    // Check if OTP is empty
    if (!otp.trim()) {
      toast.error("Please enter OTP", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    // Prevent multiple submit clicks during cooldown
    if (submitCooldown > 0) return;
    // Start submit cooldown and loading state
    startCooldown(setSubmitCooldown);
    setLoading(true);

    if (!email || !userId) {
      toast.error("Email or UserID is missing. Please try again.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/verifyOtp`,
        { email, otp },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "application/json"
          }
        }
      );


      if (response.data.success) {
        toast.success(response.data.message || "OTP verified successfully!", {
          position: toast.POSITION.TOP_RIGHT,
        });
        localStorage.setItem("email", email);
        navigate("/create-password", { state: { email, userId, signUp: location.state?.signUp }, replace: true });
      } else {
        toast.error(response.data.message || "Incorrect OTP. Please try again.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      const errorMessage = error.response?.data?.message || "Something went wrong. Please try again.";
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    // Prevent multiple resend clicks during cooldown
    if (resendCooldown > 0) return;
    if (!email || !userId) {
      toast.error("Email or UserID is missing. Cannot resend OTP.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    // Start resend cooldown
    startCooldown(setResendCooldown);

    try {
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/resendOtp`,
        { email, userId },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "OTP resent successfully! Check your email.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(response.data.message || "Failed to resend OTP.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      const errorMessage = error.response?.data?.message || "Something went wrong. Please try again.";
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };
  const handleEditProfile = () => {
    navigate("/create-new-account", {
      state: {
        firstName: location.state?.firstName || "",
        lastName: location.state?.lastName || "",
        email: location.state?.email || "",
        phone: location.state?.phone || "",
      },
    });
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="credential-page"
    >
      <Container fluid>
        <Row className="justify-content-md-center" style={{ minHeight: "100vh" }}>
          {/* Left Side */}
          <Col lg={6} className="p-0 d-none d-md-block">
            <div className="left-side d-flex align-items-center justify-content-center bg-light">
              <QuoteSlider quotes={quotes} />
            </div>
          </Col>

          {/* Right Side */}
          <Col lg={6} className="right-side my-auto">
            <div className="mb-4 card-title text-center">
              <LazyLoadImage src={IconHeader} alt="logo" className="img-fluid mb-4" />
              <h5 className="fw-bolder text-dark" style={{ fontSize: '30px' }}>One Time Passcode</h5>
              {signUp && <p className="my-4 text-light" style={{ textAlign: 'left', fontSize: '18px' }}>
                We have sent the verification code to your email address <span className="fw-bold text-primary"> {email} </span>
                {!loading && <span className="bg-primary rounded-circle p-2 ms-2">
                  <span type='button' onClick={handleEditProfile} className="text-white text-decoration-none">
                    <AiOutlineEdit size={20} color="#fff" />
                  </span>
                </span>}

              </p>}
            </div>

            <Form onSubmit={handleSubmit} className="otp-screen">
              <Form.Label>One Time Passcode*</Form.Label>
              <div className="d-flex custom-otp-container mb-2">
                <OtpInput
                  value={otp}
                  onChange={handleOtpChange}
                  numInputs={5}
                  separator={<span style={{ width: "8px" }}></span>}
                  renderSeparator={<span>&nbsp;&nbsp;&nbsp;</span>}
                  isInputNum={true}
                  shouldAutoFocus={true}
                  inputStyle={{
                    background: "#E7EFF8",
                    border: "0",
                    borderRadius: "8px",
                    height: "61px",
                    width: "70px",
                    fontSize: "20px",
                    color: "#677289",
                    fontWeight: "700",
                    caretColor: "blue",
                    textAlign: "center",
                  }}
                  focusStyle={{
                    border: "none",
                    outline: "none",
                  }}
                  renderInput={(props) => <input {...props} />}
                />
              </div>

              <Button
                variant="primary"
                className="mt-4 border-0 text-white font-16 px-5 py-3 w-100"
                type="submit"
                disabled={loading || submitCooldown > 0}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Submitting...
                  </>
                ) : submitCooldown > 0 ? `Submit (${submitCooldown}s)` : "Submit"}
              </Button>


              <p className="my-5 text-center text-light">
                Haven't received the OTP?{" "}
                <span
                  className="text-secondary fw-bold"
                  style={{
                    cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
                    color: resendCooldown > 0 ? "#888" : "inherit",
                  }}
                  onClick={resendCooldown > 0 ? undefined : handleResendOTP}
                >
                  {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : "Resend"}
                </span>
              </p>
            </Form>
          </Col>
        </Row>
      </Container>
    </motion.div>
  );
}

export default OTPScreen;
